import { AsyncAnimationDelay } from 'derivita/util/animation_delay/async_animation_delay';
import { EventLike, Event } from 'goog:goog.events';
import EventTarget from 'goog:goog.events.EventTarget';

interface StateOptions {
  synchronous?: boolean;
}

export class BaseState extends EventTarget {
  private eventDispatcher_: AsyncAnimationDelay;
  private stateEvents_: Set<string>;
  private synchronous_: boolean;

  private extraEvents_: Set<EventLike> = new Set();

  private dispatchListeners = new Map<EventTarget, string[]>();

  private synchronousTarget?: EventTarget;

  /**
   *
   * @param event
   * @param options Consider using getSynchronousTarget instead of supplying the synchronous flag
   */
  constructor(event: string | string[], options: StateOptions = {}) {
    super();
    const events = typeof event === 'string' ? [event] : event;
    this.stateEvents_ = new Set(events);
    this.synchronous_ = !!options.synchronous;

    this.eventDispatcher_ = new AsyncAnimationDelay(() => {
      this.dispatchEvents();
    });
  }

  private dispatchEvents(): void {
    const eventsToDispatch = [...this.stateEvents_, ...this.extraEvents_];
    this.extraEvents_.clear();
    eventsToDispatch.forEach((e) => {
      this.dispatchEvent(e);
    });
  }

  protected dispatchChangeEvent(extraEvent?: EventLike): void {
    if (extraEvent) {
      this.extraEvents_.add(extraEvent);
      this.synchronousTarget?.dispatchEvent(extraEvent);
    }

    if (this.synchronousTarget) {
      for (const eventLike of this.stateEvents_) {
        const event =
          typeof eventLike === 'string'
            ? new Event(eventLike, this)
            : eventLike;
        this.synchronousTarget.dispatchEvent(event);
      }
    }

    if (this.synchronous_) {
      this.dispatchEvents();
    } else {
      this.eventDispatcher_.startIfNotActive();
    }
  }

  /**
   * Repeat specific events from the specified target
   * When the specified event is emitted, this object will
   * synchronously dispatch the specified event as well as
   * dispatching its own change event. This allows us to roll-up
   * changes that are reflected in the current objects methods and
   * notify listeners of the change.
   *
   * @param target An EventTarget that we should listen to
   * @param event The name of the event we should listen for
   */
  protected dispatchOn(target: EventTarget, event: string): void {
    const events = this.dispatchListeners.get(target) ?? [];
    if (events.includes(event)) {
      return;
    }
    events.push(event);
    this.dispatchListeners.set(target, events);

    const sync =
      target instanceof BaseState ? target.getSynchronousTarget() : target;

    sync.addEventListener(event, this.dispatchChangeEvent, false, this);
  }

  /**
   * Stop listening for events from the specified target. This function
   * removes listeners registered using dispatchOn
   *
   * @param target The EventTarget that we should stop listening to.
   *    If no target is specified this function will do nothing. This allows
   *    us to conveniently handle properties that are possibly undefined without
   *    needing to check if they are set before calling this method.
   */
  protected stopDispatchingOn(target?: EventTarget): void {
    if (!target) {
      return;
    }
    const events = this.dispatchListeners.get(target);
    if (!events) {
      return;
    }

    const syncTarget =
      target instanceof BaseState ? target.getSynchronousTarget() : target;

    events.forEach((event) => {
      syncTarget.removeEventListener(
        event,
        this.dispatchChangeEvent,
        false,
        this
      );
    });
    this.dispatchListeners.delete(target);
  }

  getSynchronousTarget(): EventTarget {
    if (!this.synchronousTarget) {
      this.synchronousTarget = new EventTarget();
    }

    return this.synchronousTarget;
  }
}
